import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'

type Status = 'submitted' | 'in_review' | 'approved' | 'rejected'
type Type =
  | 'majorare_capital'
  | 'schimbare_sediu'
  | 'cesiune_parti'
  | 'numire_administrator'
  | 'modificare_obiect'
  | 'altele'

interface OnrcChange {
  id: number
  type: Type
  submissionDate: string
  registrationDate: string | null
  dossierNo: string | null
  notary: string | null
  cost: string | null
  docLink: string | null
  status: Status
  createdAt: string
}

const TYPE_LABELS: Record<Type, string> = {
  majorare_capital: 'Majorare capital',
  schimbare_sediu: 'Schimbare sediu',
  cesiune_parti: 'Cesiune părți',
  numire_administrator: 'Numire administrator',
  modificare_obiect: 'Modificare obiect activitate',
  altele: 'Alta',
}

const STATUS_LABELS: Record<Status, string> = {
  submitted: 'Depusă',
  in_review: 'În verificare',
  approved: 'Aprobată',
  rejected: 'Respinsă',
}
const STATUS_COLORS: Record<Status, string> = {
  submitted: 'bg-b4l-bg text-b4l-text-muted',
  in_review: 'bg-b4l-amber-light text-b4l-amber-dark',
  approved: 'bg-b4l-green-light text-b4l-green-dark',
  rejected: 'bg-b4l-red-light text-b4l-red-dark',
}

export function OnrcPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('onrc:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<OnrcChange | null>(null)

  const query = useQuery<{ changes: OnrcChange[] }>({
    queryKey: ['onrc'],
    queryFn: async () => (await api.get<{ changes: OnrcChange[] }>('/onrc')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Partial<OnrcChange>) => (await api.post('/onrc', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onrc'] })
      toast.success('Modificare adăugată')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<OnrcChange> }) =>
      (await api.patch(`/onrc/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onrc'] })
      toast.success('Salvat')
      setEditTarget(null)
    },
    onError: showError,
  })

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: Status }) =>
      (await api.patch(`/onrc/${id}`, { status })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onrc'] }),
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/onrc/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onrc'] })
      toast.success('Șters')
    },
  })

  const changes = useMemo(() => query.data?.changes ?? [], [query.data])
  const pag = useClientPagination(changes, 10)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Modificări ONRC</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Modificare nouă</Button>}
      </div>

      <Card description="Registrul modificărilor depuse la ONRC: data depunerii, dosar, notar, cost, stare și link la documentul atașat.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : changes.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Nicio modificare. {canWrite && 'Apasă „+ Modificare nouă”.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">Depus</th>
                  <th className="py-2 pr-3 font-medium">Înregistrat</th>
                  <th className="py-2 pr-3 font-medium">Tip</th>
                  <th className="py-2 pr-3 font-medium">Dosar</th>
                  <th className="py-2 pr-3 font-medium">Notar</th>
                  <th className="py-2 pr-3 font-medium text-right">Cost</th>
                  <th className="py-2 pr-3 font-medium">Stare</th>
                  <th className="py-2 pr-3 font-medium">Doc</th>
                  {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((c) => (
                  <tr key={c.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 text-xs whitespace-nowrap">
                      {new Date(c.submissionDate).toLocaleDateString('en-GB')}
                    </td>
                    <td className="py-2 pr-3 text-xs whitespace-nowrap">
                      {c.registrationDate
                        ? new Date(c.registrationDate).toLocaleDateString('en-GB')
                        : '—'}
                    </td>
                    <td className="py-2 pr-3 text-xs">{TYPE_LABELS[c.type]}</td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{c.dossierNo ?? '—'}</td>
                    <td className="py-2 pr-3 text-xs">{c.notary ?? '—'}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-right">
                      {c.cost ? Number(c.cost).toFixed(2) : '—'}
                    </td>
                    <td className="py-2 pr-3">
                      {canWrite ? (
                        <select
                          value={c.status}
                          onChange={(e) =>
                            statusMutation.mutate({ id: c.id, status: e.target.value as Status })
                          }
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[c.status]}`}
                        >
                          {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[c.status]}`}
                        >
                          {STATUS_LABELS[c.status]}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {c.docLink ? (
                        <a
                          href={c.docLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-b4l-blue-dark underline"
                        >
                          deschide ↗
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    {canWrite && (
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(c)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Ștergi modificarea ${TYPE_LABELS[c.type]}?`))
                                deleteMutation.mutate(c.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="modificări"
          />
        </div>
      </Card>

      {createOpen && (
        <OnrcFormModal
          title="Modificare ONRC nouă"
          submitLabel="Adaugă"
          onClose={() => setCreateOpen(false)}
          onSubmit={(d) => createMutation.mutate(d)}
          submitting={createMutation.isPending}
        />
      )}
      {editTarget && (
        <OnrcFormModal
          title={`Editează ${TYPE_LABELS[editTarget.type]}`}
          submitLabel="Salvează"
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(d) => updateMutation.mutate({ id: editTarget.id, data: d })}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

function OnrcFormModal({
  title,
  submitLabel,
  initial,
  onClose,
  onSubmit,
  submitting,
}: {
  title: string
  submitLabel: string
  initial?: OnrcChange
  onClose: () => void
  onSubmit: (d: Partial<OnrcChange>) => void
  submitting: boolean
}) {
  const [type, setType] = useState<Type>(initial?.type ?? 'majorare_capital')
  const [submissionDate, setSubmissionDate] = useState(
    initial?.submissionDate ? initial.submissionDate.slice(0, 10) : '',
  )
  const [registrationDate, setRegistrationDate] = useState(
    initial?.registrationDate ? initial.registrationDate.slice(0, 10) : '',
  )
  const [dossierNo, setDossierNo] = useState(initial?.dossierNo ?? '')
  const [notary, setNotary] = useState(initial?.notary ?? '')
  const [cost, setCost] = useState(initial?.cost ?? '')
  const [docLink, setDocLink] = useState(initial?.docLink ?? '')

  const submit = () => {
    if (!submissionDate) {
      toast.error('Data depunerii e obligatorie')
      return
    }
    onSubmit({
      type,
      submissionDate: new Date(submissionDate).toISOString(),
      registrationDate: registrationDate ? new Date(registrationDate).toISOString() : null,
      dossierNo: dossierNo.trim() || null,
      notary: notary.trim() || null,
      cost: cost ? String(Number(cost)) : null,
      docLink: docLink.trim() || null,
    } as unknown as Partial<OnrcChange>)
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Tip</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as Type)}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          >
            {(Object.keys(TYPE_LABELS) as Type[]).map((x) => (
              <option key={x} value={x}>
                {TYPE_LABELS[x]}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Data depunere"
            type="date"
            value={submissionDate}
            onChange={(e) => setSubmissionDate(e.target.value)}
          />
          <Field
            label="Data înregistrare (opțional)"
            type="date"
            value={registrationDate}
            onChange={(e) => setRegistrationDate(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Nr. dosar"
            value={dossierNo}
            onChange={(e) => setDossierNo(e.target.value)}
          />
          <Field label="Notar" value={notary} onChange={(e) => setNotary(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Cost RON"
            type="number"
            step="0.01"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
          />
          <Field
            label="Link document"
            value={docLink}
            onChange={(e) => setDocLink(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
